import React, { useEffect, useMemo } from "react"
import { Page, Banner } from "@shopify/polaris"
import Loading from "components/widgets/Loading"
import { useAppContext } from "components/AppContext"
import AnalyzeTemplate from "views/templates/AnalyzeTemplate"
import {
  PILLAR_ENVIRONMENTAL,
  PILLAR_PROFIT,
  PILLAR_SOCIAL,
} from "utils/constants/pillars"
import ObjectiveNavigationTemplate from "views/templates/ObjectiveNavigationTemplate"
import withAuthRequired from "../../components/auth/withAuthRequired"
import usePillars from "views/organisms/pillars/usePillars"
import { useParams, useHistory } from "react-router-dom"
import ThemeTabs from "components/widgets/ThemeTabs"
import DataQualityWorkplaceChartOrganism from "views/organisms/canvases/charts/dataQuality/DataQualityWorkplaceChartOrganism"
import DataQualityDatacenterInternalChartOrganism from "views/organisms/canvases/charts/dataQuality/DataQualityDatacenterInternalChartOrganism"
import DataQualityNetworkChartOrganism from "views/organisms/canvases/charts/dataQuality/DataQualityNetworkChartOrganism"
import { useTranslation } from "react-i18next"
import DataQualityCloudChartOrganism from "views/organisms/canvases/charts/dataQuality/DataQualityCloudChartOrganism"
import DataQualitySaasChartOrganism from "views/organisms/canvases/charts/dataQuality/DataQualitySaasChartOrganism"
import AccessModal from "components/AccessModal"

const THEME_COMPONENTS = {
  workplace: DataQualityWorkplaceChartOrganism,
  datacenter_internal: DataQualityDatacenterInternalChartOrganism,
  network: DataQualityNetworkChartOrganism,
  cloud: DataQualityCloudChartOrganism,
  software: DataQualitySaasChartOrganism,
}

// Themes that are always disabled (not yet implemented)
const STATICALLY_DISABLED_THEMES = [
  "datacenter_external",
  "it_team",
  "business_assets",
  "purchased_it_services",
  "ai_services",
]

// All possible themes for data quality
const ALL_QUALITY_THEMES = [
  "workplace",
  "network",
  "datacenter_internal",
  "cloud",
  "software",
  ...STATICALLY_DISABLED_THEMES,
]

const DataQualityPage = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { pillarId, themeId } = useParams()
  const {
    currentPeriod,
    parentOrganization,
    isCurrentNodeLoading,
    isEnvCollectLoading,
    envCollect,
    subscription,
    currentOrganization,
    currentUser,
  } = useAppContext()

  const hasOverviewAccess = subscription?.features?.data_quality_overview

  const paywallTexts = {
    description: t(
      "common:subscription_feature_missing.data_quality_overview.description"
    ),
    mailSubject: t(
      "common:subscription_feature_missing.data_quality_overview.mailTemplateSubject",
      { currentOrganizationName: currentOrganization?.name || "" }
    ),
    mailBody: t(
      "common:subscription_feature_missing.data_quality_overview.mailTemplateBody",
      {
        currentUserFirstname: currentUser?.firstname || "",
        currentUserLastname: currentUser?.lastname || "",
      }
    ),
  }

  const pillars = usePillars({
    pageName: "/collect/overview/environmental",
    [PILLAR_SOCIAL]: { disabled: true },
    [PILLAR_PROFIT]: { disabled: true },
  })

  const objectives = [
    {
      objectiveId: 13,
      selected: true,
      url: "/collect/overview/environmental/13",
    },
  ]

  // Themes with inventory on this node or anywhere in its subtree (from serializer).
  const availableThemes = useMemo(
    () => envCollect?.data_quality_inventory_themes ?? [],
    [envCollect]
  )

  // Combine statically disabled themes with dynamically disabled themes
  const disabledThemes = useMemo(() => {
    const dynamicallyDisabled = ALL_QUALITY_THEMES.filter(
      (theme) =>
        !STATICALLY_DISABLED_THEMES.includes(theme) &&
        !availableThemes.includes(theme)
    )
    return [...STATICALLY_DISABLED_THEMES, ...dynamicallyDisabled]
  }, [availableThemes])

  const hasAnyInventory = availableThemes.length > 0

  // Redirect to environmental because only environmental
  // is available for the moment
  useEffect(() => {
    if (pillarId !== PILLAR_ENVIRONMENTAL) {
      window.location.href = "/collect/overview/environmental"
    }
  }, [pillarId])

  // Redirect to first available theme if current theme is not available
  useEffect(() => {
    if (
      !isCurrentNodeLoading &&
      envCollect &&
      hasAnyInventory &&
      themeId &&
      !availableThemes.includes(themeId)
    ) {
      history.push(`/collect/overview/environmental/13/${availableThemes[0]}`)
    }
  }, [
    themeId,
    availableThemes,
    hasAnyInventory,
    isCurrentNodeLoading,
    envCollect,
    history,
  ])

  // Redirect to first available theme if no theme is selected
  useEffect(() => {
    if (
      !isCurrentNodeLoading &&
      envCollect &&
      hasAnyInventory &&
      !themeId &&
      pillarId === PILLAR_ENVIRONMENTAL
    ) {
      history.push(`/collect/overview/environmental/13/${availableThemes[0]}`)
    }
  }, [
    themeId,
    pillarId,
    availableThemes,
    hasAnyInventory,
    isCurrentNodeLoading,
    envCollect,
    history,
  ])

  if (isCurrentNodeLoading || isEnvCollectLoading) {
    return <Loading />
  }

  return (
    <ObjectiveNavigationTemplate>
      <Page fullWidth>
        <AnalyzeTemplate
          parentOrganization={parentOrganization}
          themeType={PILLAR_ENVIRONMENTAL}
          pillars={pillars}
          objectives={objectives}
          tabsComponent={
            <ThemeTabs
              pageName="collect/overview"
              pillar={PILLAR_ENVIRONMENTAL}
              objective="13"
              theme={themeId}
              collect={envCollect}
              disabledThemes={disabledThemes}
            />
          }
          periodName={currentPeriod?.name}
        >
          {!hasOverviewAccess ? (
            <AccessModal open scoped texts={paywallTexts} />
          ) : !hasAnyInventory ? (
            <div style={{ padding: "20px" }}>
              <Banner
                title={t(
                  "collect:data_quality.no_inventory_banner.title",
                  "No inventory loaded"
                )}
                status="warning"
              >
                <p>
                  {t(
                    "collect:data_quality.no_inventory_banner.message",
                    "Please load your workplace, datacenter or network inventories first, then come back to review the data quality."
                  )}
                </p>
              </Banner>
            </div>
          ) : (
            (() => {
              const ThemeComponent =
                THEME_COMPONENTS[themeId] || THEME_COMPONENTS.workplace
              return <ThemeComponent />
            })()
          )}
        </AnalyzeTemplate>
      </Page>
    </ObjectiveNavigationTemplate>
  )
}

export default withAuthRequired(DataQualityPage)
